// Detail page's "Transactions" section — the product's movement history with
// running balance, unit cost and value (Unleashed "Stock Enquiry" parity,
// Stuart's 2026-08-26 ruling; unit cost/value columns added in the follow-up
// full-parity pass — the API already returned unitCostCents/valueCents per
// row, just not rendered until now). GET /reporting/movements?productId=&
// withRunningBalance=true (services/api/src/modules/reporting/movements.ts's
// Products-module addon).
// Split out from ProductDetailScreen so it's independently testable (goal
// text: "a test that the transactions table shows runningQty").
//
// Filters + Previous/Next cursor pagination mirror features/reports/screens/
// MovementLedgerReport.tsx's idiom exactly (read first, per the goal brief) —
// duplicated rather than imported (see api.ts/csv.ts/format.ts headers for why
// this feature keeps its own copies). Reference rendering (`referenceType
// referenceId.slice(-6)`, plain text) matches that screen's own choice not to
// link out — no generic reference->route mapping exists yet to build one
// safely.
import { useState } from 'react';
import { Badge, Button, Group, Select, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconArrowDownRight, IconArrowUpRight, IconListSearch, IconMinus } from '@tabler/icons-react';
import { MOVEMENT_TYPES, type BaseUom } from '@zebra/core';
import { EmptyState } from '../../../components/EmptyState';
import { MOVEMENT_TYPE_DESCRIPTIONS, MOVEMENT_TYPE_LABELS } from '../../../lib/enumLabels';
import { rangeQuery, useProductMovements, useSites } from '../api';
import { useCsvDownload } from '../csv';
import { formatCostPerUom, formatDateTime, formatMoneyCents, formatQty } from '../format';

const TYPE_OPTIONS = MOVEMENT_TYPES.map((t) => ({ value: t, label: MOVEMENT_TYPE_LABELS[t] }));

function QtyCell({ qty }: { qty: string }) {
  const n = Number(qty);
  const color = n > 0 ? 'brandGreen' : n < 0 ? 'red' : 'dimmed';
  const Icon = n > 0 ? IconArrowUpRight : n < 0 ? IconArrowDownRight : IconMinus;
  return (
    <Group gap={4} justify="flex-end" wrap="nowrap">
      <Icon size={14} />
      <Text c={color}>{formatQty(qty)}</Text>
    </Group>
  );
}

export function ProductTransactionsTable({ productId, baseUom }: { productId: string; baseUom: BaseUom }) {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);

  // Cursor is page-position state, not product-identity state — if the route
  // swaps $productId without remounting this component, a stale cursor from
  // the previous product must never reach the query. Reset it synchronously
  // during render (React's documented "adjusting state on a prop change"
  // pattern) rather than in a useEffect: an effect-based reset would still let
  // this render's useProductMovements call fire once with the stale cursor
  // before the effect corrects it on the next pass — this way the corrected
  // value is what the very first render for the new productId ever queries.
  // Same fix as ProductCostHistoryTable's identical pagination idiom.
  const [cursorProductId, setCursorProductId] = useState(productId);
  if (productId !== cursorProductId) {
    setCursorProductId(productId);
    setCursors([undefined]);
  }
  const cursor = cursors[cursors.length - 1];

  const sitesQuery = useSites();
  const query = useProductMovements({ productId, siteId: siteId ?? undefined, type: type ?? undefined, from, to, cursor, limit: 50 });
  const download = useCsvDownload();
  const [csvLoading, setCsvLoading] = useState(false);
  const rows = query.data?.items ?? [];

  function resetPaging() {
    setCursors([undefined]);
  }

  async function handleCsv() {
    setCsvLoading(true);
    try {
      await download(
        '/reporting/movements',
        {
          productId,
          siteId: siteId ?? undefined,
          type: type ?? undefined,
          ...rangeQuery(from, to),
          withRunningBalance: 'true',
        },
        'product-movements.csv',
      );
    } finally {
      setCsvLoading(false);
    }
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Title order={4}>Transactions</Title>
        <Button variant="light" loading={csvLoading} onClick={handleCsv}>
          Download CSV
        </Button>
      </Group>

      <Group gap="md" wrap="wrap" align="flex-end">
        <Select
          label="Site"
          placeholder="All sites"
          clearable
          data={(sitesQuery.data?.items ?? []).map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }))}
          value={siteId}
          onChange={(v) => {
            setSiteId(v);
            resetPaging();
          }}
          w={200}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          label="Type"
          placeholder="All types"
          clearable
          data={TYPE_OPTIONS}
          value={type}
          onChange={(v) => {
            setType(v);
            resetPaging();
          }}
          w={180}
          comboboxProps={{ withinPortal: true }}
        />
        <DatePickerInput
          label="From"
          placeholder="Start date"
          value={from}
          onChange={(v) => {
            setFrom(v);
            resetPaging();
          }}
          maxDate={to ?? undefined}
          clearable
          w={160}
        />
        <DatePickerInput
          label="To"
          placeholder="End date"
          value={to}
          onChange={(v) => {
            setTo(v);
            resetPaging();
          }}
          minDate={from ?? undefined}
          clearable
          w={160}
        />
      </Group>

      <Table.ScrollContainer minWidth={780}>
        <Table verticalSpacing="xs" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Date</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Location</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Qty</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Running balance</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Unit cost</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Value</Table.Th>
              <Table.Th>Reference</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>{formatDateTime(r.occurredAt)}</Table.Td>
                <Table.Td>
                  <Tooltip label={MOVEMENT_TYPE_DESCRIPTIONS[r.type]} openDelay={300}>
                    <Badge variant="light">{MOVEMENT_TYPE_LABELS[r.type]}</Badge>
                  </Tooltip>
                </Table.Td>
                <Table.Td>{r.locationCode}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <QtyCell qty={r.qty} />
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{r.runningQty !== undefined ? formatQty(r.runningQty) : '—'}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatCostPerUom(r.unitCostCents, baseUom)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatMoneyCents(r.valueCents)}</Table.Td>
                <Table.Td>
                  {r.referenceType} {r.referenceId.slice(-6)}
                </Table.Td>
              </Table.Tr>
            ))}
            {!query.isLoading && rows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <EmptyState
                    icon={IconListSearch}
                    title="No transactions for the selected filters"
                    hint="Try widening the date range or clearing a filter."
                  />
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group justify="space-between">
        <Button variant="default" size="sm" disabled={cursors.length <= 1} onClick={() => setCursors((prev) => prev.slice(0, -1))}>
          Previous page
        </Button>
        <Button variant="default" size="sm" disabled={!query.data?.nextCursor} onClick={() => setCursors((prev) => [...prev, query.data?.nextCursor ?? undefined])}>
          Next page
        </Button>
      </Group>
    </Stack>
  );
}
